import { useState, useMemo, useEffect } from 'react'
import { Pressable } from '@ayble-health/dashboard-components'
import { Button } from 'components/ui/button'
import type { Medication } from '../../models/careOSPrototype'
import { useMedications } from '../../api/careOSPrototype/useMedications'
import MedicationRow, { type MedicationAction } from './MedicationRow'
import AddMedicationForm, { type NewMedicationDraft } from './AddMedicationForm'
import { ChevronDown, PlusIcon } from './icons'

// Pharmacy tab — the patient's e-prescriptions, medications, and supplements,
// in their own sections.
//
// Prototype scope: display, add, archive. Like LabsTab, this seeds local
// editable state from the query once and then mutates client-side; there's no
// backend for add/archive yet, so nothing persists past a reload.

const TODAY_LABEL = 'Aug 7, 2026' // display-only, matches the fixtures' hardcoded style

function SuccessToast({ title, detail, onDismiss }: {
  title: string
  detail: string
  onDismiss: () => void
}) {
  return (
    <div style={{
      position: 'fixed', top: 64, right: 20, zIndex: 9999, width: 300,
      background: 'var(--canvas)', borderRadius: 'var(--radius-card)',
      border: '1px solid var(--scrubs)', borderLeft: '3px solid var(--scrubs)',
      padding: '13px 14px', display: 'flex', alignItems: 'flex-start', gap: 10,
      boxShadow: '0 4px 20px rgba(4,51,56,0.12)',
      animation: 'pharmToastSlideIn 220ms ease-out',
    }}>
      <div style={{ flexShrink: 0, marginTop: 1 }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7.25" stroke="#6CCCC6" strokeWidth="1.5" />
          <path d="M5 8l2.2 2.2L11 5.5" stroke="#6CCCC6" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontSize: 'var(--text-cos-btn-sb-size)', fontWeight: 'var(--text-cos-btn-sb-weight)',
          color: 'var(--evergreen)', fontFamily: 'var(--font-body)', margin: '0 0 2px', lineHeight: 1.3,
        }}>{title}</p>
        <p style={{
          fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)',
          fontFamily: 'var(--font-body)', margin: 0, lineHeight: 1.4,
        }}>{detail}</p>
      </div>
      <Pressable
        onClick={onDismiss}
        aria-label="Dismiss"
        flexShrink={0}
        color="steel"
        p={0}
        mt="1px"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M2 2l8 8M10 2L2 10" />
        </svg>
      </Pressable>
    </div>
  )
}

function SectionHeading({ label, count }: { label: string; count: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 10px' }}>
      <span style={{
        fontSize: 'var(--text-caption-size)', fontWeight: 'var(--text-caption-weight)',
        letterSpacing: 'var(--text-caption-tracking)', textTransform: 'uppercase',
        color: 'var(--blue-grey)', fontFamily: 'var(--font-body)',
      }}>
        {label}
      </span>
      <span style={{
        fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)',
        fontFamily: 'var(--font-body)',
      }}>
        {count}
      </span>
    </div>
  )
}

export default function PharmacyTab({ patientId }: { patientId: string }) {
  const { medications: fetched, isLoading } = useMedications(patientId)

  // Seed-once local state: the query is the source of truth until the provider
  // touches something, after which local edits win (add/archive have no
  // backend). Same read-through-with-override posture as LabsTab.
  const [override, setOverride] = useState<Medication[] | null>(null)
  const meds = override ?? fetched

  const [formOpen, setFormOpen] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [toast, setToast] = useState<{ title: string; detail: string } | null>(null)

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 3200)
    return () => clearTimeout(t)
  }, [toast])

  // E-prescribed entries get their own section regardless of kind, so an
  // e-prescribed medication shows there instead of under Medications. Archived
  // status trumps all three — one shared "no longer taking" list, independent
  // of kind or e-prescribing.
  const { activeEPrescriptions, activeMedications, activeSupplements, archived } = useMemo(() => ({
    activeEPrescriptions: meds.filter(m => m.status === 'active' && m.ePrescribed),
    activeMedications: meds.filter(m => m.status === 'active' && !m.ePrescribed && m.kind === 'medication'),
    activeSupplements: meds.filter(m => m.status === 'active' && !m.ePrescribed && m.kind === 'supplement'),
    archived: meds.filter(m => m.status === 'archived'),
  }), [meds])

  const mutate = (fn: (list: Medication[]) => Medication[]) =>
    setOverride(fn(meds))

  const handleAdd = (draft: NewMedicationDraft) => {
    const entry: Medication = {
      id: `local-${draft.name.toLowerCase().replace(/\s+/g, '-')}-${meds.length}`,
      kind: draft.kind,
      name: draft.name,
      dose: draft.dose || undefined,
      status: 'active',
      addedDate: TODAY_LABEL,
      instructions: draft.instructions || undefined,
      notes: draft.notes || undefined,
    }
    mutate(list => [entry, ...list])
    setFormOpen(false)
    setToast({
      title: `${draft.name} added`,
      detail: draft.dose ? `${draft.dose} · ${draft.kind}` : `No dose recorded · ${draft.kind}`,
    })
  }

  const handleAction = (med: Medication, action: MedicationAction) => {
    mutate(list => list.map(m => {
      if (m.id !== med.id) return m
      switch (action) {
        case 'archive': return { ...m, status: 'archived', archivedDate: TODAY_LABEL }
        case 'restore': return { ...m, status: 'active', archivedDate: undefined }
        default:        return m
      }
    }))

    if (action === 'archive') {
      setArchiveOpen(true)
      setToast({ title: `${med.name} moved to no longer taking`, detail: `Stopped ${TODAY_LABEL}` })
    }
  }

  return (
    <div style={{ padding: '20px 20px 40px', flex: 1 }}>
      <style>{`
        @keyframes pharmFadeIn { from { opacity: 0; transform: translateY(-4px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes pharmToastSlideIn { from { opacity: 0; transform: translateX(24px) } to { opacity: 1; transform: translateX(0) } }
      `}</style>

      {toast && <SuccessToast title={toast.title} detail={toast.detail} onDismiss={() => setToast(null)} />}

      {/* Tab header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
        <h2 style={{
          fontSize: 'var(--text-md-b-size)', fontWeight: 'var(--text-md-b-weight)',
          color: 'var(--evergreen)', fontFamily: 'var(--font-body)', margin: 0,
        }}>
          Pharmacy
        </h2>
        <div style={{ marginLeft: 'auto' }}>
          <Button
            variant="outline"
            onClick={() => setFormOpen(true)}
            disabled={formOpen}
            gap="6px"
          >
            <PlusIcon size={9} strokeWidth={1.4} />
            Add Medication
          </Button>
        </div>
      </div>

      {/* Summary line — the one thing a PharmD wants before reading the list */}
      <p style={{
        fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)',
        fontFamily: 'var(--font-body)', margin: '0 0 16px',
      }}>
        {isLoading
          ? 'Loading…'
          : [
              `${activeEPrescriptions.length} e-prescription${activeEPrescriptions.length === 1 ? '' : 's'}`,
              `${activeMedications.length} medication${activeMedications.length === 1 ? '' : 's'}`,
              `${activeSupplements.length} supplement${activeSupplements.length === 1 ? '' : 's'}`,
              // Only surface the count that carries a signal — a lean list
              // shouldn't advertise "0 no longer taking".
              archived.length > 0 ? `${archived.length} no longer taking` : null,
            ].filter(Boolean).join(' · ')}
      </p>

      {formOpen && <AddMedicationForm onSubmit={handleAdd} onCancel={() => setFormOpen(false)} />}

      {/* E-Prescriptions */}
      <SectionHeading label="E-Prescriptions" count={activeEPrescriptions.length} />
      {activeEPrescriptions.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
          {activeEPrescriptions.map(med => (
            <MedicationRow key={med.id} med={med} onAction={a => handleAction(med, a)} />
          ))}
        </div>
      ) : (
        <div style={{
          border: '1.5px dashed var(--cloud)', borderRadius: 'var(--radius-card)',
          padding: '28px 12px', textAlign: 'center', background: 'var(--bg-subtle)',
          marginBottom: 24,
        }}>
          <span style={{
            fontSize: 'var(--text-xs-size)', color: 'var(--steel)', fontFamily: 'var(--font-body)',
          }}>
            {isLoading ? 'Loading e-prescriptions…' : 'No e-prescriptions recorded'}
          </span>
        </div>
      )}

      {/* Medications */}
      <SectionHeading label="Medications" count={activeMedications.length} />
      {activeMedications.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
          {activeMedications.map(med => (
            <MedicationRow key={med.id} med={med} onAction={a => handleAction(med, a)} />
          ))}
        </div>
      ) : (
        <div style={{
          border: '1.5px dashed var(--cloud)', borderRadius: 'var(--radius-card)',
          padding: '28px 12px', textAlign: 'center', background: 'var(--bg-subtle)',
          marginBottom: 24,
        }}>
          <span style={{
            fontSize: 'var(--text-xs-size)', color: 'var(--steel)', fontFamily: 'var(--font-body)',
          }}>
            {isLoading ? 'Loading medications…' : 'No medications recorded'}
          </span>
        </div>
      )}

      {/* Supplements */}
      <SectionHeading label="Supplements" count={activeSupplements.length} />
      {activeSupplements.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
          {activeSupplements.map(med => (
            <MedicationRow key={med.id} med={med} onAction={a => handleAction(med, a)} />
          ))}
        </div>
      ) : (
        <div style={{
          border: '1.5px dashed var(--cloud)', borderRadius: 'var(--radius-card)',
          padding: '28px 12px', textAlign: 'center', background: 'var(--bg-subtle)',
          marginBottom: 24,
        }}>
          <span style={{
            fontSize: 'var(--text-xs-size)', color: 'var(--steel)', fontFamily: 'var(--font-body)',
          }}>
            {isLoading ? 'Loading supplements…' : 'No supplements recorded'}
          </span>
        </div>
      )}

      {/* No longer taking — collapsed by default; it's history, not the working list */}
      {archived.length > 0 && (
        <div>
          <Pressable
            onClick={() => setArchiveOpen(o => !o)}
            display="flex"
            alignItems="center"
            gap="6px"
            p={0}
            mb={archiveOpen ? '10px' : 0}
            color="bluegrey"
            fontFamily="body"
          >
            <span style={{
              fontSize: 'var(--text-caption-size)', fontWeight: 'var(--text-caption-weight)',
              letterSpacing: 'var(--text-caption-tracking)', textTransform: 'uppercase',
              fontFamily: 'var(--font-body)',
            }}>
              No longer taking
            </span>
            <span style={{ fontSize: 'var(--text-xs-size)', fontFamily: 'var(--font-body)' }}>
              {archived.length}
            </span>
            <span style={{
              display: 'inline-flex',
              transform: archiveOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s',
            }}>
              <ChevronDown />
            </span>
          </Pressable>

          {archiveOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {archived.map(med => (
                <MedicationRow key={med.id} med={med} onAction={a => handleAction(med, a)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
